import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Loader2, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { picklistService } from '../services';
import type { NetSuiteEmployeeDto } from '@features/users/types';

interface NetSuiteEmployeePickerProps {
  value: NetSuiteEmployeeDto | null;
  onChange: (employee: NetSuiteEmployeeDto | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Applied to the SELECTED-state box and to the search input, so a caller can match a neighbouring
   * control's height. The selected state is a two-line block with `px-3 py-2`, so its natural height
   * is taller than a plain input — which is exactly how it ended up standing taller than the Issuance
   * reason select beside it in the Post dialog.
   */
  className?: string;
}

export function NetSuiteEmployeePicker({
  className,
  value,
  onChange,
  placeholder = 'Search employee by name or ID...',
  disabled = false,
}: NetSuiteEmployeePickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<NetSuiteEmployeeDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    setIsSearching(true);
    setError(null);
    try {
      const employees = await picklistService.searchNetSuiteEmployees(term);
      setResults(employees);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to search employees';
      setError(message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void runSearch(term);
      }, 400);
    },
    [runSearch],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSelect = (employee: NetSuiteEmployeeDto) => {
    onChange(employee);
    setSearchTerm('');
    setResults([]);
    setError(null);
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm('');
    setResults([]);
    setError(null);
  };

  if (value) {
    return (
      <div className={cn('flex items-center justify-between rounded-md border bg-accent/50 px-3 py-1', className)}>
        <div className="min-w-0 text-sm">
          <div className="font-medium">
            {value.lastName}, {value.firstName}
            {value.middleName ? ` ${value.middleName}` : ''}
          </div>
          <div className="text-xs text-muted-foreground flex gap-3">
            {value.employeeIdNumber && <span>{value.employeeIdNumber}</span>}
            {value.positionName && <span>{value.positionName}</span>}
            {value.departmentName && <span>{value.departmentName}</span>}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleClear}
          disabled={disabled}
          aria-label="Clear selected employee"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          className={cn('pl-9', className)}
          value={searchTerm}
          disabled={disabled}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-[calc(50%-0.5rem)] h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {results.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded-md border">
          {results.map((emp) => (
            <button
              key={emp.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent text-sm border-b last:border-b-0 transition-colors"
              onClick={() => handleSelect(emp)}
            >
              <div className="font-medium">
                {emp.lastName}, {emp.firstName}
                {emp.middleName ? ` ${emp.middleName}` : ''}
              </div>
              <div className="text-xs text-muted-foreground flex gap-3">
                {emp.employeeIdNumber && <span>{emp.employeeIdNumber}</span>}
                {emp.positionName && <span>{emp.positionName}</span>}
                {emp.departmentName && <span>{emp.departmentName}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {searchTerm.length >= 2 && !isSearching && results.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">No employees found</p>
      )}
    </div>
  );
}
